import { Controller } from "stimulus";
import { percent, countdown } from "utils/live_poll";

// Auto-dismiss window (ms) after a terminal result (closed poll / resolved prediction).
const AUTO_DISMISS_MS = 45000;

// The floating live-poll card. Owns all card state; driven by poll events forwarded from
// the chat-panel controller (no subscription of its own) and casts votes via a dispatched
// window event. Option rows are cloned from a <template>; `sync` restores state on join.
export default class LivePoll extends Controller {
  static targets = [
    "card", "badge", "badgeLabel", "countdown", "countdownValue", "status",
    "question", "collapsed", "expandLabel", "expanded", "options", "total",
    "totalLabel", "notice", "optionTemplate",
  ];

  connect() {
    this.poll = null; // latest serialized payload
    this.myOptionId = null; // this viewer's locked pick (id), or null
    this.expandedView = false; // collapsed by default
    this.dismissed = false; // "x" dismissal is sticky for this poll id
  }

  disconnect() {
    this.stopCountdown();
    this.clearAutoDismiss();
  }

  // Entry point for every poll event forwarded by the chat panel.
  received(event) {
    const data = event.detail;
    if (!data || data.type !== "poll") return;

    switch (data.action) {
      case "sync":
        this.onSync(data);
        break;
      case "open":
        this.onOpen(data);
        break;
      case "tally":
        this.onTally(data);
        break;
      case "awaiting":
        this.onAwaiting(data);
        break;
      case "resolved":
        this.onResolved(data);
        break;
      case "closed":
        this.onClosed(data);
        break;
      case "cancelled":
      case "dismiss":
        this.onCancelled(data);
        break;
      case "vote_ack":
        this.onVoteAck(data);
        break;
      case "vote_rejected":
        this.onVoteRejected(data);
        break;
    }
  }

  // --- lifecycle handlers -------------------------------------------------

  // Join payload: the current poll in whatever status, plus this viewer's locked pick.
  onSync(data) {
    this.adoptPoll(data.poll, { reset: true });
    if (data.your_option_id != null) {
      this.myOptionId = Number(data.your_option_id);
      this.expandedView = true; // an already-voted viewer sees the result, not the compact CTA
    }
    this.renderForStatus();
  }

  // A poll just launched - fresh card, collapsed by default.
  onOpen(data) {
    this.adoptPoll(data.poll, { reset: true });
    this.renderForStatus();
  }

  // Counts changed - patch bars / percentages / total only. Ignore other polls, and ignore a
  // late tally once this card is already showing a finalized result (a tally broadcast can
  // arrive just after the closed/awaiting one it raced).
  onTally(data) {
    if (!this.poll || this.poll.status !== "open") return;
    if (!data.poll || Number(data.poll.id) !== Number(this.poll.id)) return;
    this.poll = data.poll;
    this.renderTally();
  }

  onAwaiting(data) {
    this.adoptPoll(data.poll);
    this.stopCountdown();
    this.renderForStatus();
  }

  onResolved(data) {
    this.adoptPoll(data.poll);
    this.revealFinalResult();
    this.stopCountdown();
    this.renderForStatus();
    this.scheduleAutoDismiss();
  }

  onClosed(data) {
    this.adoptPoll(data.poll);
    this.revealFinalResult();
    this.stopCountdown();
    this.renderForStatus();
    this.scheduleAutoDismiss();
  }

  onCancelled(data) {
    if (data.poll && this.poll && Number(data.poll.id) !== Number(this.poll.id)) return;
    this.remove();
  }

  // Server confirmation of this viewer's own vote. Lock to the recorded option whether
  // it was newly recorded or a duplicate (the pick is final either way).
  onVoteAck(data) {
    if (!this.poll || Number(data.poll_id) !== Number(this.poll.id)) return;
    this.myOptionId = Number(data.option_id);
    this.applyCounts(data.counts); // reflect the voter's own vote at once, ahead of the throttled room tally
    this.hideNotice();
    this.renderTally();
  }

  // Vote was refused (e.g. arrived after close). Unlock so the options are usable again
  // and surface a brief inline reason.
  onVoteRejected(data) {
    if (!this.poll || Number(data.poll_id) !== Number(this.poll.id)) return;
    this.myOptionId = null;
    this.showNotice(this.reasonText(data.reason));
    this.renderTally();
  }

  // --- voting / controls --------------------------------------------------

  vote(event) {
    if (!this.poll || this.poll.status !== "open") return;
    if (this.votingClosed()) return; // deadline passed; the close broadcast is imminent
    if (this.myOptionId != null) return; // already locked

    const button = event.currentTarget;
    const optionId = Number(button.dataset.optionId);
    if (Number.isNaN(optionId)) return;

    // Optimistically lock immediately; the server vote_ack confirms (or vote_rejected unlocks).
    this.myOptionId = optionId;
    this.hideNotice();
    this.renderTally();

    this.dispatch("vote", {
      target: window,
      detail: { pollId: this.poll.id, optionId },
    });
  }

  // The local countdown has reached the deadline but the authoritative close broadcast
  // hasn't landed yet (e.g. CloseJob latency). Treat voting as closed so we neither send a
  // vote the server will reject nor flicker the optimistic lock.
  votingClosed() {
    const result = countdown(this.poll && this.poll.closes_at);
    return result != null && result.remaining === 0;
  }

  // "Vote" affordance: reveal options, countdown, and tallies.
  expand() {
    this.expandedView = true;
    this.renderForStatus();
  }

  // "Hide": collapse back to the compact title state; the card stays present.
  collapse() {
    this.expandedView = false;
    this.renderForStatus();
  }

  // "x": dismiss this card from this viewer only. Client-side presentation; no server call.
  // A dismissal made before the final result is in is undone by revealFinalResult.
  dismiss() {
    this.dismissed = true;
    this.hide();
  }

  // A viewer who dismissed the card before the result was in should still see the outcome:
  // when the final result lands (poll closed / prediction resolved) bring a dismissed card
  // back, expanded so the results are visible. No-op once a fresh poll has reset state.
  revealFinalResult() {
    if (!this.dismissed) return;
    this.dismissed = false;
    this.expandedView = true;
  }

  // --- rendering ----------------------------------------------------------

  adoptPoll(payload, { reset = false } = {}) {
    if (!payload) return;

    const changedPoll = !this.poll || Number(this.poll.id) !== Number(payload.id);
    this.poll = payload;

    if (reset || changedPoll) {
      // A new poll occupies the slot: clear any prior dismissal / pick / collapse state.
      this.dismissed = false;
      this.myOptionId = null;
      this.expandedView = false;
      this.hideNotice();
      this.clearAutoDismiss();
    }
  }

  renderForStatus() {
    if (this.dismissed || !this.poll) return;

    this.show();
    this.renderHeader();
    this.questionTarget.textContent = this.poll.question || "";
    this.renderOptions();
    this.renderTally();
    this.renderExpansion();
    this.renderCountdownState();
  }

  renderHeader() {
    const prediction = this.poll.kind === "prediction";
    this.badgeLabelTarget.textContent = prediction ? "Live Prediction" : "Live Poll";
    this.expandLabelTarget.textContent = prediction ? "Predict" : "Vote";
  }

  // Rebuild option rows from the template. Only rebuild when the option set changes so
  // a high-frequency tally just patches existing rows (renderTally).
  renderOptions() {
    const ids = (this.poll.options || []).map((o) => String(o.id)).join(",");
    if (this.renderedOptionIds === ids) return;
    this.renderedOptionIds = ids;

    this.optionsTarget.replaceChildren();
    (this.poll.options || []).forEach((option) => {
      const node = this.optionTemplateTarget.content.cloneNode(true);
      const button = node.querySelector("[data-live-poll-option]");
      button.dataset.optionId = option.id;
      node.querySelector("[data-live-poll-label]").textContent = option.label;
      this.optionsTarget.appendChild(node);
    });
  }

  // Merge a Redis-style { option_id: count } snapshot (from a vote_ack) into the current
  // payload so the voter sees their own vote reflected immediately, before the throttled
  // room tally arrives (~1s later).
  applyCounts(counts) {
    if (!counts || !this.poll) return;

    let total = 0;
    (this.poll.options || []).forEach((option) => {
      const count = Number(counts[String(option.id)]);
      if (!Number.isNaN(count)) option.count = count;
      total += Number(option.count) || 0;
    });
    this.poll.total_votes = total;
  }

  // Patch counts, percentages, bar widths, total, the locked-pick highlight, and (when
  // resolved) the correct-option highlight. Safe to call on every tally.
  renderTally() {
    if (!this.poll) return;

    const total = Number(this.poll.total_votes) || 0;
    const open = this.poll.status === "open";
    const closed = this.votingClosed();
    const resolved = this.poll.kind === "prediction" && this.poll.correct_option_id != null;
    const locked = this.myOptionId != null;

    this.totalTarget.textContent = total;
    this.totalLabelTarget.textContent = total === 1 ? "vote" : "votes";

    (this.poll.options || []).forEach((option) => {
      const row = this.optionsTarget.querySelector(`[data-option-id="${option.id}"]`);
      if (!row) return;

      const count = Number(option.count) || 0;
      const pct = percent(count, total);
      const bar = row.querySelector("[data-live-poll-bar]");
      const percentEl = row.querySelector("[data-live-poll-percent]");
      const check = row.querySelector("[data-live-poll-icon]");

      if (bar) bar.style.width = `${pct}%`;
      if (percentEl) percentEl.textContent = `${pct}%`;

      const isMine = locked && Number(this.myOptionId) === Number(option.id);
      const isCorrect = resolved && Number(this.poll.correct_option_id) === Number(option.id);

      // The user's own pick is highlighted; once resolved the correct option is highlighted
      // (green) and a wrong personal pick is muted red.
      row.classList.toggle("border-accent", isMine && !resolved);
      row.classList.toggle("ring-1", isMine || isCorrect);
      row.classList.toggle("ring-accent", isMine && !resolved);
      row.classList.toggle("border-green-500", isCorrect);
      row.classList.toggle("ring-green-500", isCorrect);
      // The themed: resting border outranks the highlight borders above, so it steps aside for them.
      row.classList.toggle("themed:border-light-text/20", !(isMine && !resolved) && !isCorrect);
      if (check) check.classList.toggle("hidden", !isMine);

      // Rows are inert once the user has locked a pick, the poll is no longer open, or the
      // local countdown has elapsed (the authoritative close broadcast may lag 0:00 briefly).
      row.disabled = !open || locked || closed;
    });

    this.renderPersonalResult(resolved);
  }

  // Personal "you were right / wrong" line, shown only on a resolved prediction the
  // viewer voted in.
  renderPersonalResult(resolved) {
    if (!resolved || this.myOptionId == null) {
      this.statusTarget.classList.add("hidden");
      return;
    }

    const correct = Number(this.myOptionId) === Number(this.poll.correct_option_id);
    this.statusTarget.textContent = correct ? "You were right" : "You were wrong";
    this.statusTarget.classList.remove("hidden", "text-amber-600");
    this.statusTarget.classList.add(correct ? "text-green-600" : "text-red-600");
    this.countdownTarget.classList.add("hidden");
  }

  renderExpansion() {
    this.collapsedTarget.classList.toggle("hidden", this.expandedView);
    this.expandedTarget.classList.toggle("hidden", !this.expandedView);
  }

  // Countdown only ticks while the poll is open; awaiting shows a status line instead.
  renderCountdownState() {
    if (this.poll.status === "open" && this.poll.closes_at) {
      this.statusTarget.classList.add("hidden");
      this.countdownTarget.classList.remove("hidden");
      this.countdownTarget.classList.add("flex");
      this.startCountdown();
    } else {
      this.countdownTarget.classList.add("hidden");
      this.countdownTarget.classList.remove("flex");
      this.stopCountdown();
      this.renderTerminalStatus();
    }
  }

  // Non-personal status text for awaiting / closed states (the personal win/loss line,
  // set in renderPersonalResult, takes precedence when present).
  renderTerminalStatus() {
    if (!this.statusTarget.classList.contains("hidden")) return; // personal result already shown

    if (this.poll.status === "awaiting_result") {
      this.statusTarget.textContent = "Voting closed - awaiting result";
      this.statusTarget.classList.remove("hidden", "text-green-600", "text-red-600");
      this.statusTarget.classList.add("text-amber-600");
    } else if (this.poll.status === "closed") {
      this.statusTarget.textContent = "Final result";
      this.statusTarget.classList.remove("hidden", "text-green-600", "text-red-600");
      this.statusTarget.classList.add("text-amber-600");
    }
  }

  // --- countdown ----------------------------------------------------------

  startCountdown() {
    if (this.ticker) return;
    this.tick();
    this.ticker = window.setInterval(() => this.tick(), 1000);
  }

  stopCountdown() {
    if (this.ticker) {
      window.clearInterval(this.ticker);
      this.ticker = null;
    }
  }

  tick() {
    const result = countdown(this.poll && this.poll.closes_at);
    if (!result) return;

    this.countdownValueTarget.textContent = result.label;

    // The authoritative close arrives via a `closed`/`awaiting` broadcast; we stop the visible
    // ticker at 0 and re-render so the options disable immediately even if that broadcast lags.
    if (result.remaining === 0) {
      this.stopCountdown();
      this.renderTally();
    }
  }

  // --- auto-dismiss / visibility -----------------------------------------

  scheduleAutoDismiss() {
    this.clearAutoDismiss();
    this.autoDismiss = window.setTimeout(() => this.remove(), AUTO_DISMISS_MS);
  }

  clearAutoDismiss() {
    if (this.autoDismiss) {
      window.clearTimeout(this.autoDismiss);
      this.autoDismiss = null;
    }
  }

  // Remove the card entirely (cancelled or auto-dismiss): clear state and hide.
  remove() {
    this.stopCountdown();
    this.clearAutoDismiss();
    this.poll = null;
    this.renderedOptionIds = null;
    this.hide();
  }

  show() {
    this.cardTarget.classList.remove("hidden");
    this.offsetBelowAnnouncement();
  }

  // Anchor the overlay just below the announcement banner (when one is showing) so it
  // never covers it; otherwise pin to the top of the message area. The banner lives in
  // the same chat panel root and is toggled with `hidden`.
  offsetBelowAnnouncement() {
    const banner = this.element.parentElement
      && this.element.parentElement.querySelector("[data-chat-panel-target='announcement']");
    const visible = banner && !banner.classList.contains("hidden");
    this.cardTarget.style.top = visible ? `${banner.offsetHeight}px` : "0px";
  }

  hide() {
    this.cardTarget.classList.add("hidden");
  }

  showNotice(text) {
    this.noticeTarget.textContent = text;
    this.noticeTarget.classList.remove("hidden");
  }

  hideNotice() {
    this.noticeTarget.classList.add("hidden");
  }

  // The server sends a user-facing reason sentence (e.g. "Voting is closed."); show it
  // directly, falling back to a generic message only when none was provided.
  reasonText(reason) {
    return reason && reason.trim() !== "" ? reason : "Your vote couldn't be recorded.";
  }
}
